import { autorun } from 'mobx';
import { Chart as ChartJS } from 'chart.js';

import globalFiltersStore from '../stores/GlobalFiltersStore';
import timelineEventsStore from '../stores/TimelineEventsStore';


const DAY_MS = 24 * 60 * 60 * 1000;

export const parseChartDate = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.getTime();
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 100000000000 ? value : value * 1000;
  }
  if (typeof value !== 'string') return null;

  const iso = value.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));

  const ru = value.match(/(\d{2})\.(\d{2})\.(\d{4})/);
  if (ru) return Date.UTC(Number(ru[3]), Number(ru[2]) - 1, Number(ru[1]));
  return null;
};

const formatDate = (timestamp) => new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'UTC',
}).format(new Date(timestamp));

const selectedEvents = () => {
  const selected = new Set(globalFiltersStore.selectedTimelineEventIds);
  return timelineEventsStore.events.filter((event) => selected.has(event.id));
};

const getDateAnchors = (chart) => {
  const xScale = chart.scales.x || Object.values(chart.scales).find((scale) => scale.axis === 'x');
  if (!xScale) return [];

  const labels = chart.data.labels || [];
  const anchors = labels.map((label, index) => {
    const timestamp = parseChartDate(label);
    const scaleValue = xScale.type === 'category' ? index : timestamp;
    return {
      timestamp,
      x: timestamp === null ? null : xScale.getPixelForValue(scaleValue),
      index,
    };
  }).filter((item) => item.timestamp !== null && Number.isFinite(item.x));

  return anchors.sort((a, b) => a.timestamp - b.timestamp);
};

const interpolateX = (anchors, timestamp) => {
  if (anchors.length === 0) return null;
  if (timestamp < anchors[0].timestamp || timestamp > anchors[anchors.length - 1].timestamp) {
    return null;
  }

  const exact = anchors.find((anchor) => Math.abs(anchor.timestamp - timestamp) < DAY_MS / 2);
  if (exact) return exact.x;

  for (let index = 1; index < anchors.length; index += 1) {
    const left = anchors[index - 1];
    const right = anchors[index];
    if (timestamp > left.timestamp && timestamp < right.timestamp) {
      const ratio = (timestamp - left.timestamp) / (right.timestamp - left.timestamp);
      return left.x + ((right.x - left.x) * ratio);
    }
  }
  return null;
};

const interpolateSeriesY = (chart, x) => {
  const seriesMeta = chart.getSortedVisibleDatasetMetas().find(
    (meta) => meta.data?.length > 0
  );
  const points = (seriesMeta?.data || [])
    .filter((point) => !point.skip && Number.isFinite(point.x) && Number.isFinite(point.y))
    .sort((a, b) => a.x - b.x);

  if (points.length === 0) return chart.chartArea.top + 10;
  if (x <= points[0].x) return points[0].y;
  if (x >= points[points.length - 1].x) return points[points.length - 1].y;

  for (let index = 1; index < points.length; index += 1) {
    const left = points[index - 1];
    const right = points[index];
    if (x >= left.x && x <= right.x) {
      const ratio = right.x === left.x ? 0 : (x - left.x) / (right.x - left.x);
      return left.y + ((right.y - left.y) * ratio);
    }
  }
  return chart.chartArea.top + 10;
};

const groupEventsByDate = (events) => {
  const groups = new Map();
  events.forEach((event) => {
    const timestamp = parseChartDate(event.date);
    if (timestamp === null) return;
    const key = String(timestamp);
    if (!groups.has(key)) groups.set(key, { timestamp, events: [] });
    groups.get(key).events.push(event);
  });
  return [...groups.values()].sort((a, b) => a.timestamp - b.timestamp);
};

const truncate = (value, maxLength = 64) => (
  value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value
);

const drawTooltip = (chart, marker) => {
  const { ctx, chartArea } = chart;
  const visibleEvents = marker.events.slice(0, 5);
  const lines = [
    formatDate(marker.timestamp),
    ...visibleEvents.map((event) => `${event.title}${event.owner_username ? ` · ${event.owner_username}` : ''}`),
  ];
  if (marker.events.length > visibleEvents.length) {
    lines.push(`Ещё событий: ${marker.events.length - visibleEvents.length}`);
  }

  ctx.save();
  ctx.font = '13px sans-serif';
  const padding = 10;
  const lineHeight = 19;
  const textLines = lines.map((line) => truncate(line));
  const width = Math.min(
    420,
    Math.max(180, ...textLines.map((line) => ctx.measureText(line).width)) + padding * 2
  );
  const height = textLines.length * lineHeight + padding * 2;
  let left = marker.x + 12;
  if (left + width > chartArea.right) left = marker.x - width - 12;
  left = Math.max(chartArea.left, left);
  let top = marker.y - height / 2;
  top = Math.max(chartArea.top, Math.min(top, chartArea.bottom - height));

  ctx.fillStyle = 'rgba(255, 255, 255, 0.98)';
  ctx.strokeStyle = '#9aa4ad';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(left, top, width, height, 5);
  ctx.fill();
  ctx.stroke();

  textLines.forEach((line, index) => {
    ctx.font = index === 0 ? 'bold 13px sans-serif' : '13px sans-serif';
    ctx.fillStyle = '#1f2933';
    ctx.fillText(line, left + padding, top + padding + lineHeight * (index + 0.78));
  });
  ctx.restore();
};

export const timelineEventsPlugin = {
  id: 'timelineEvents',
  afterDatasetsDraw(chart) {
    if (chart.options?.plugins?.timelineEvents?.display === false) return;
    const anchors = getDateAnchors(chart);
    if (anchors.length < 2) {
      chart.$timelineEventMarkers = [];
      return;
    }

    const groups = groupEventsByDate(selectedEvents());
    const markers = groups.map((group) => {
      const x = interpolateX(anchors, group.timestamp);
      if (x === null) return null;
      return {
        ...group,
        x,
        y: interpolateSeriesY(chart, x),
      };
    }).filter(Boolean);
    chart.$timelineEventMarkers = markers;

    const { ctx, chartArea } = chart;
    ctx.save();
    markers.forEach((marker) => {
      const hasOwn = marker.events.some((event) => event.is_owner);
      ctx.strokeStyle = hasOwn ? 'rgba(25, 118, 210, 0.62)' : 'rgba(239, 108, 0, 0.62)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(marker.x, chartArea.top);
      ctx.lineTo(marker.x, chartArea.bottom);
      ctx.stroke();

      ctx.fillStyle = '#111827';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(marker.x, marker.y, marker.events.length > 1 ? 7 : 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      if (marker.events.length > 1) {
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 9px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(Math.min(marker.events.length, 9)), marker.x, marker.y + 0.5);
      }
    });
    ctx.restore();
  },
  afterDraw(chart) {
    if (chart.$timelineEventHover) drawTooltip(chart, chart.$timelineEventHover);
  },
  afterEvent(chart, args) {
    const event = args.event;
    const markers = chart.$timelineEventMarkers || [];
    const hovered = event.type === 'mouseout'
      ? null
      : markers.find((marker) => Math.hypot(marker.x - event.x, marker.y - event.y) <= 10) || null;
    if (hovered !== chart.$timelineEventHover) {
      chart.$timelineEventHover = hovered;
      if (chart.canvas) chart.canvas.style.cursor = hovered ? 'pointer' : '';
      args.changed = true;
    }
  },
};

let registered = false;

export const registerTimelineEventsPlugin = () => {
  if (registered) return;
  registered = true;
  ChartJS.register(timelineEventsPlugin);

  autorun(() => {
    timelineEventsStore.events
      .map((event) => (
        `${event.id}:${event.date}:${event.title}:${event.description}:${event.owner_username}:${event.is_owner}`
      ))
      .join('|');
    globalFiltersStore.selectedTimelineEventIds.join(',');
    Object.values(ChartJS.instances || {}).forEach((chart) => chart.update('none'));
  });
};
